/* ck-brand-merchcards (filename kept as ck-brand-pullquotes for back-compat)
   Editorial merchandising cards inserted into brand-page collection grid.
   Supports multiple card types at mixed sizes. Re-fires on AJAX filter swap.

   Card types:
     - pullquote   (2x1) — type-only, multi-line serif + credit
     - manifesto   (1x1) — three-word brand line, stacked
     - editor      (1x1) — staff-pick stamp, cream stamp register
     - most_loved  (1x1) — live data, highest-reviewed product in the brand
*/
(function () {
  if (!document.body.classList.contains('template-collection-brand')) return;

  // Static cards (pullquote, manifesto, editor) are compiled from
  // scripts/merch-pipeline/data/*.json by build-merch-data.js into
  // assets/ck-brand-pullquotes-data.js (sets window.CK_MERCH_CARDS_BY_BRAND).
  // Live cards (most_loved, bestseller) are emitted inline from Liquid in
  // ck-brand-hero.liquid using current collection data — they're not stored
  // in the JSON because their data is computed per-render.
  const CARDS_BY_BRAND = (typeof window !== 'undefined' && window.CK_MERCH_CARDS_BY_BRAND) || {};
  const LIVE_CARDS = (typeof window !== 'undefined' && window.CK_MERCH_LIVE_CARDS) || [];

  // Editor-style placement: deliberately uneven positions so the layout reads
  // as chosen, not algorithmic. Capped low — pull quotes are rare on purpose.
  function positionsForCount(count, cardCount) {
    if (count < 12) return [];
    if (count < 24) return [5].slice(0, cardCount);
    if (count < 40) return [4, 18].slice(0, cardCount);
    if (count < 64) return [4, 14, 26, 38].slice(0, cardCount);
    return [4, 14, 28, 44, 60, 76].slice(0, cardCount);
  }

  function brandHandle() {
    const m = location.pathname.match(/\/collections\/([^/?]+)/);
    return m ? m[1].toLowerCase() : null;
  }

  function gridContainer() {
    const item = document.querySelector('.template-collection-brand .product-item');
    return item ? item.parentElement : null;
  }

  function clearExisting(container) {
    container.querySelectorAll('.ck-merch-card').forEach((n) => n.remove());
  }

  function pullquoteFaceHtml(quote) {
    const lines = (quote.lines || []).map((l) => `<div class="ck-merch-pullquote__line">${l}</div>`).join('');
    return `${lines}<div class="ck-merch-pullquote__credit">— ${quote.credit}</div>`;
  }

  function buildPullquote(card) {
    // Normalise: legacy single-quote shape (lines + credit on the card) → quotes array
    const quotes = (card.quotes && card.quotes.length)
      ? card.quotes
      : [{ lines: card.lines || [], credit: card.credit || '' }];
    const front = quotes[0];
    const back = quotes[1] || quotes[0];
    return `
      <div class="ck-merch-pullquote-flip">
        <div class="ck-merch-pullquote-flip__inner">
          <div class="ck-merch-pullquote-flip__face ck-merch-pullquote-flip__face--front">
            <div class="ck-merch-pullquote">${pullquoteFaceHtml(front)}</div>
          </div>
          <div class="ck-merch-pullquote-flip__face ck-merch-pullquote-flip__face--back">
            <div class="ck-merch-pullquote">${pullquoteFaceHtml(back)}</div>
          </div>
        </div>
      </div>
    `;
  }

  function attachPullquoteFlip(node, card) {
    const quotes = (card.quotes && card.quotes.length) ? card.quotes : null;
    if (!quotes || quotes.length < 2) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const flip = node.querySelector('.ck-merch-pullquote-flip');
    const front = node.querySelector('.ck-merch-pullquote-flip__face--front .ck-merch-pullquote');
    const back = node.querySelector('.ck-merch-pullquote-flip__face--back .ck-merch-pullquote');
    if (!flip || !front || !back) return;

    let idx = 1; // index of the most-recently-assigned face content
    let flipped = false; // true cycle state — independent of hover override
    let hovered = false;
    let timer = null;
    let pending = null;

    function applyFlipClass() {
      flip.classList.toggle('is-flipped', hovered ? !flipped : flipped);
    }

    function tick() {
      if (hovered) return; // pause natural cycle while user is hovering
      flipped = !flipped;
      applyFlipClass();
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        idx = (idx + 1) % quotes.length;
        const target = flipped ? front : back;
        target.innerHTML = pullquoteFaceHtml(quotes[idx]);
      }, 1250);
    }

    node.addEventListener('mouseenter', () => {
      hovered = true;
      applyFlipClass();
    });
    node.addEventListener('mouseleave', () => {
      hovered = false;
      applyFlipClass();
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !timer) {
            timer = setInterval(tick, 10000);
          } else if (!entry.isIntersecting && timer) {
            clearInterval(timer);
            timer = null;
          }
        });
      }, { threshold: 0.25 });
      io.observe(node);
    } else {
      timer = setInterval(tick, 10000);
    }
  }

  function buildManifesto(card) {
    const words = card.words.map((w) => `<div class="ck-merch-manifesto__word">${w}</div>`).join('');
    const brand = card.brand ? `<div class="ck-merch-manifesto__brand">${card.brand}</div>` : '';
    const meta = card.meta ? `<div class="ck-merch-manifesto__meta">${card.meta}</div>` : '';
    return `
      <div class="ck-merch-manifesto">
        <div class="ck-merch-manifesto__top">
          ${brand}
          <div class="ck-merch-manifesto__rule"></div>
        </div>
        <div class="ck-merch-manifesto__words">${words}</div>
        ${meta ? `
          <div class="ck-merch-manifesto__bottom">
            <div class="ck-merch-manifesto__rule"></div>
            ${meta}
          </div>
        ` : ''}
      </div>
    `;
  }

  function buildEditor(card) {
    return `
      <div class="ck-merch-editor">
        <div class="ck-merch-editor__label">${card.label}</div>
        <div class="ck-merch-editor__accent">${card.accent}</div>
        ${card.meta ? `<div class="ck-merch-editor__meta">${card.meta}</div>` : ''}
      </div>
    `;
  }

  function buildFeatureBand(card) {
    const products = (card.products || []).slice(0, 3);
    if (!products.length) return '';
    const productHtml = products
      .map((p) => {
        const ratingNum = Number(p.rating) || 0;
        const ratingText = ratingNum > 0 ? ratingNum.toFixed(1) : '';
        const countNum = Number(p.count) || 0;
        const reviewLabel = countNum === 1 ? '1 review' : `${countNum} reviews`;
        return `
          <a class="ck-merch-band__product" href="${p.url}">
            <div class="ck-merch-band__thumb">
              <img src="${p.image}" alt="${p.title}" loading="lazy" />
            </div>
            <div class="ck-merch-band__product-text">
              <div class="ck-merch-band__product-title">${p.title}</div>
              <div class="ck-merch-band__product-meta">
                <span class="ck-merch-band__product-price">${p.price}</span>
                ${ratingText ? `<span class="ck-merch-band__product-rating">★ ${ratingText} <span class="ck-merch-band__product-count">(${countNum})</span></span>` : ''}
              </div>
            </div>
          </a>
        `;
      })
      .join('');
    return `
      <div class="ck-merch-band__editorial">
        <div class="ck-merch-band__title">${card.title}</div>
        <div class="ck-merch-band__rule"></div>
        ${card.copy ? `<div class="ck-merch-band__copy">${card.copy}</div>` : ''}
      </div>
      <div class="ck-merch-band__products">
        ${productHtml}
      </div>
    `;
  }

  function buildWordmark(card) {
    const name = (card.brand_name || '').toString();
    const safeName = name.replace(/[<>&"]/g, '');
    const url = card.collection_url || '#';
    const heroStyle = card.hero_image ? `background-image: url('${card.hero_image}');` : '';
    return `
      <a class="ck-wordmark" href="${url}">
        <span class="ck-wordmark__bg" aria-hidden="true" style="${heroStyle}"></span>
        <span class="ck-wordmark__text" style="${heroStyle}">${safeName}</span>
        <span class="ck-wordmark__stamp">EDITORIAL</span>
      </a>
    `;
  }

  function renderFeatureBlockProducts(products) {
    return products.slice(0, 3).map((p, idx) => {
      const ratingNum = Number(p.rating) || 0;
      const countNum = Number(p.count) || 0;
      const filled = Math.round(ratingNum);
      const starsHtml = ratingNum > 0
        ? `<span class="ck-feature-block__product-stars" aria-label="Rating ${ratingNum} of 5">${'★'.repeat(filled)}<span class="ck-feature-block__product-stars-empty">${'★'.repeat(5 - filled)}</span><span class="ck-feature-block__product-rating">(${countNum})</span></span>`
        : '';
      const variants = Number(p.variants_count) || 0;
      const variantsHtml = variants > 1 ? `<span class="ck-feature-block__product-variance">${variants} Sizes</span>` : '';
      const onSale = p.compare_at_price && p.compare_at_price !== p.price;
      const priceHtml = onSale
        ? `<span class="ck-feature-block__product-price">${p.price}</span><span class="ck-feature-block__product-save">Save ${p.save_amount || ''}</span>`
        : `<span class="ck-feature-block__product-price">${p.price}</span>`;
      const hasCutout = !!p.cutout;
      const thumbHtml = hasCutout
        ? `<div class="ck-feature-block__product-thumb ck-feature-block__product-thumb--cutout"><img class="ck-feature-block__product-cutout" src="${p.cutout}" alt="" loading="lazy"></div>`
        : `<div class="ck-feature-block__product-thumb" style="background-image: url('${p.image}');"></div>`;
      return `
        <a class="ck-feature-block__product${hasCutout ? ' ck-feature-block__product--cutout' : ''}" href="${p.url}">
          <span class="ck-feature-block__product-num" aria-hidden="true">${idx + 1}</span>
          ${thumbHtml}
          <div class="ck-feature-block__product-text">
            <span class="ck-feature-block__product-title">${p.title}</span>
            <div class="ck-feature-block__product-meta">
              ${variantsHtml}
              ${priceHtml}
            </div>
          </div>
        </a>
      `;
    }).join('');
  }

  function buildFeatureBlock(card) {
    let tabs = card.tabs;
    if (!tabs || !tabs.length) {
      tabs = [{
        key: 'default',
        label: card.title || '',
        copy: card.copy || '',
        products: card.products || []
      }];
    }
    tabs = tabs.filter((t) => (t.products || []).length > 0);
    if (!tabs.length) return '';

    const ICONS = {
      best_sellers: `<svg class="ck-feature-block__tab-icon" viewBox="0 0 40 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 31 C 7 22, 3 14, 8 9 C 12 5, 18 6, 20 12 C 22 6, 28 5, 32 9 C 37 14, 33 22, 20 31 Z"/></svg>`,
      sale: `<svg class="ck-feature-block__tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41 13.41 20.59a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor"/></svg>`,
      new_in: `<svg class="ck-feature-block__tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v18M3 12h18M5.5 5.5l13 13M5.5 18.5l13-13"/></svg>`
    };
    const tabIcon = (key) => ICONS[key] || '';
    const headTitle = tabs.length > 1
      ? `<div class="ck-feature-block__tabs" role="tablist">${tabs.map((t, i) => `<button type="button" class="ck-feature-block__tab${i === 0 ? ' is-active' : ''}" data-tab-key="${t.key}" role="tab" aria-selected="${i === 0 ? 'true' : 'false'}">${tabIcon(t.key)}<span class="ck-feature-block__tab-label">${t.label}</span></button>`).join('')}</div>`
      : `<h2 class="ck-feature-block__title">${tabIcon(tabs[0].key)}${tabs[0].label}</h2>`;

    const panels = tabs.map((t, i) => `
      <div class="ck-feature-block__products${i === 0 ? ' is-active' : ''}" data-panel-key="${t.key}">
        ${renderFeatureBlockProducts(t.products || [])}
      </div>
    `).join('');

    const copyByKey = {};
    tabs.forEach((t) => { copyByKey[t.key] = t.copy || ''; });
    const copyPayload = encodeURIComponent(JSON.stringify(copyByKey));

    return `
      <div class="ck-feature-block__left">
        <div class="ck-feature-block__head">
          ${card.eyebrow ? `<div class="ck-feature-block__eyebrow">${card.eyebrow}</div>` : ''}
          ${headTitle}
        </div>
        <p class="ck-feature-block__copy" data-tab-copy data-tab-copy-map="${copyPayload}">${tabs[0].copy || ''}</p>
        ${card.hero_video
          ? `<div class="ck-feature-block__hero ck-feature-block__hero--video"${card.hero_image ? ` style="background-image: url('${card.hero_image}');"` : ''}><video class="ck-feature-block__hero-video" autoplay muted loop playsinline${card.hero_image ? ` poster="${card.hero_image}"` : ''} aria-hidden="true"><source src="${card.hero_video}" type="video/mp4"></video></div>`
          : (card.hero_image ? `<div class="ck-feature-block__hero" style="background-image: url('${card.hero_image}');"></div>` : '')}
      </div>
      <div class="ck-feature-block__right">
        ${card.shop_label ? `<div class="ck-feature-block__shop-head"><svg class="ck-feature-block__shop-icon" viewBox="0 0 40 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 31 C 7 22, 3 14, 8 9 C 12 5, 18 6, 20 12 C 22 6, 28 5, 32 9 C 37 14, 33 22, 20 31 Z"/></svg><span>${card.shop_label}</span></div>` : ''}
        <div class="ck-feature-block__panels">
          ${panels}
        </div>
      </div>
    `;
  }

  function attachFeatureBlockTabs(node) {
    const tabs = Array.from(node.querySelectorAll('.ck-feature-block__tab'));
    if (tabs.length < 2) return;
    const panels = Array.from(node.querySelectorAll('.ck-feature-block__products'));
    const copyEl = node.querySelector('[data-tab-copy]');
    const hero = node.querySelector('.ck-feature-block__hero');
    let copyMap = {};
    if (copyEl && copyEl.dataset.tabCopyMap) {
      try { copyMap = JSON.parse(decodeURIComponent(copyEl.dataset.tabCopyMap)); } catch (e) { copyMap = {}; }
    }
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const key = tab.dataset.tabKey;
        if (tab.classList.contains('is-active')) return;
        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach((p) => {
          p.classList.toggle('is-active', p.dataset.panelKey === key);
        });
        if (copyEl && copyMap[key] != null) {
          copyEl.textContent = copyMap[key];
        }
        // Sheet pass-over the brand hero on tab swap
        if (hero) {
          hero.classList.remove('is-swapping');
          // Force reflow so the animation restarts on rapid clicks
          void hero.offsetWidth;
          hero.classList.add('is-swapping');
        }
      });
    });
  }

  function buildListCard(card) {
    const products = (card.products || []).slice(0, 3);
    if (!products.length) return '';
    const items = products
      .map((p, idx) => {
        const num = String(idx + 1).padStart(2, '0');
        return `
          <a class="ck-merch-list__item" href="${p.url}" data-bleed="${p.image}">
            <span class="ck-merch-list__num">${num}</span>
            <span class="ck-merch-list__name">${p.title}</span>
          </a>
        `;
      })
      .join('');
    const cta = card.cta_url
      ? `<a class="ck-merch-list__cta" href="${card.cta_url}">${card.cta_label || 'Shop the range →'}</a>`
      : '';
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const eyebrow = card.eyebrow_static || `Updated ${hh}:${mm}`;
    return `
      <div class="ck-merch-list">
        <div class="ck-merch-list__head">
          <span class="ck-merch-list__eyebrow">${eyebrow}</span>
          <span class="ck-merch-list__title">${card.title}</span>
        </div>
        <div class="ck-merch-list__rule"></div>
        <div class="ck-merch-list__items">${items}</div>
        ${cta}
        <div class="ck-merch-list__bleed" aria-hidden="true"></div>
      </div>
    `;
  }

  function attachListInteractions(node) {
    const items = Array.from(node.querySelectorAll('.ck-merch-list__item'));
    if (!items.length) return;
    let activeIdx = 0;
    let timer = null;
    let paused = false;

    function show(idx) {
      // Brentford-style progress reset: kill transition, force reflow, restart
      items.forEach((it) => {
        it.classList.add('ck-progress-reset');
        it.classList.remove('is-active');
      });
      void node.offsetHeight;
      items.forEach((it) => it.classList.remove('ck-progress-reset'));

      const active = items[idx];
      if (!active) return;
      active.classList.add('is-active');
      const img = active.dataset.bleed;
      if (img) node.style.setProperty('--bleed-img', `url("${img}")`);
      node.classList.add('is-bleeding');
    }

    function tick() {
      if (paused) return;
      activeIdx = (activeIdx + 1) % items.length;
      show(activeIdx);
    }

    function start() {
      show(activeIdx);
      timer = setInterval(tick, 2400);
    }

    items.forEach((item, i) => {
      item.addEventListener('mouseenter', () => {
        paused = true;
        activeIdx = i;
        show(i);
      });
    });
    node.addEventListener('mouseleave', () => {
      paused = false;
    });

    // Only cycle when card is in viewport (perf + respects reduced motion)
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      show(0);
      return;
    }
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !timer) {
            start();
          } else if (!entry.isIntersecting && timer) {
            clearInterval(timer);
            timer = null;
          }
        });
      }, { threshold: 0.25 });
      io.observe(node);
    } else {
      start();
    }
  }

  function buildCard(card) {
    const wrap = document.createElement('div');
    wrap.className = `ck-merch-card ck-merch-card--${card.size} ck-merch-card--${card.type}`;
    let inner = '';
    switch (card.type) {
      case 'pullquote':
        inner = buildPullquote(card);
        break;
      case 'manifesto':
        inner = buildManifesto(card);
        break;
      case 'editor':
        inner = buildEditor(card);
        break;
      case 'feature_band':
        inner = buildFeatureBand(card);
        break;
      case 'feature_block':
        inner = buildFeatureBlock(card);
        break;
      case 'wordmark':
        inner = buildWordmark(card);
        break;
      case 'list_card':
        inner = buildListCard(card);
        break;
      default:
        return null;
    }
    wrap.innerHTML = inner;
    if (card.type === 'list_card') {
      // schedule after the node is in the DOM
      requestAnimationFrame(() => attachListInteractions(wrap));
    }
    if (card.type === 'feature_block') {
      requestAnimationFrame(() => attachFeatureBlockTabs(wrap));
    }
    if (card.type === 'pullquote') {
      requestAnimationFrame(() => attachPullquoteFlip(wrap, card));
    }
    return wrap;
  }

  function injectCards() {
    const handle = brandHandle();
    const staticCards = CARDS_BY_BRAND[handle] || [];
    // Spread three card types across the grid: FB up top (row 3-ish), pullquote
    // mid-grid, wordmark down low — so the editorial moments aren't bunched together.
    const liveFB = LIVE_CARDS.filter((c) => c.type === 'feature_block');
    const liveWordmark = LIVE_CARDS.filter((c) => c.type === 'wordmark');
    const liveOther = LIVE_CARDS.filter((c) => c.type !== 'feature_block' && c.type !== 'wordmark');
    const cards = [...liveFB, ...staticCards, ...liveWordmark, ...liveOther];
    if (!cards.length) return;

    const container = gridContainer();
    if (!container) return;

    clearExisting(container);

    const items = Array.from(container.querySelectorAll('.product-item'));
    const positions = positionsForCount(items.length, cards.length);

    positions.forEach((pos, i) => {
      const card = cards[i];
      let actualPos = pos;
      // feature_block lands on row 3 (shift one row of 5 cols down)
      if (card && card.type === 'feature_block') actualPos = pos + 5;
      // pullquote drops two rows below default so it doesn't sit right under FB
      if (card && card.type === 'pullquote') actualPos = pos + 10;
      // wordmark drops well down the grid so the three card types are spread
      if (card && card.type === 'wordmark') actualPos = pos + 12;
      const anchor = items[actualPos - 1];
      if (!anchor || !card) return;
      const node = buildCard(card);
      if (!node) return;
      anchor.parentNode.insertBefore(node, anchor.nextSibling);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectCards);
  } else {
    injectCards();
  }

  document.addEventListener('krown:main-collection-grid:filtered', () => {
    setTimeout(injectCards, 50);
  });
  document.addEventListener('ck:grid:replaced', () => {
    setTimeout(injectCards, 50);
  });

  // Strip the brand name from the start of product titles on the brand page.
  // The full title remains in the source HTML for crawlers/SEO; we only edit
  // the visible text node on the brand page where the prefix is redundant.
  function stripBrandFromTitles() {
    const brandEl = document.querySelector('[data-brand-name]');
    const brandName = brandEl && brandEl.textContent ? brandEl.textContent.trim() : '';
    if (!brandName) return;
    const escaped = brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('^' + escaped + '\\s+', 'i');
    const titles = document.querySelectorAll('.template-collection-brand .product-item__title span');
    titles.forEach((node) => {
      if (node.dataset.ckTitleStripped === '1') return;
      const text = (node.textContent || '').trim();
      if (re.test(text)) {
        node.textContent = text.replace(re, '');
        node.dataset.ckTitleStripped = '1';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stripBrandFromTitles);
  } else {
    stripBrandFromTitles();
  }
  document.addEventListener('krown:main-collection-grid:filtered', () => setTimeout(stripBrandFromTitles, 50));
  document.addEventListener('ck:grid:replaced', () => setTimeout(stripBrandFromTitles, 50));
})();
